import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { salesAPI } from '../../services/api';
import { Sale, PaymentMethod, SaleStatus } from '../../types';
import { useTranslation } from 'react-i18next';
import { useCompany } from '../CompanyContext';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  UserIcon,
  DocumentTextIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
  CalendarDaysIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

const SaleDetail: React.FC = () => {
  const { t } = useTranslation();
  const { selectedCompany } = useCompany();
  const { id } = useParams<{ id: string }>();
  const [sale, setSale] = useState<Sale | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadSale();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSale = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await salesAPI.getById(id!);
      setSale(data);
    } catch (err) {
      setError('Failed to load sale');
      console.error('Error loading sale:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    const currency = selectedCompany?.defaultCurrency?.code || 'USD';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: currency 
    }).format(Number(amount || 0));
  };

  const formatDateTime = (date?: Date | string | null) =>
    date
      ? new Date(date).toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const getStatusIcon = (status: SaleStatus) => {
    switch (status) {
      case SaleStatus.PENDING:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case SaleStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case SaleStatus.CANCELLED:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case SaleStatus.REFUNDED:
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatPaymentMethod = (method: PaymentMethod) =>
    method.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const getSaleType = (sale: Sale) => {
    if (sale.orderId) return 'order';
    if (sale.subscriptionId) return 'subscription';
    if (sale.loanId) return 'loan';
    return 'standalone';
  };

  const getSaleTypeDisplay = (sale: Sale) => {
    const type = getSaleType(sale);
    switch (type) {
      case 'order':
        return {
          label: t('sales.types.order'),
          icon: '📦',
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          linkTo: `/orders/${sale.orderId}`,
          reference: `#${sale.orderId?.slice(0, 8)}`
        };
      case 'subscription':
        return {
          label: t('sales.types.subscription'),
          icon: '🔄',
          color: 'text-purple-600',
          bgColor: 'bg-purple-100',
          linkTo: `/subscriptions/${sale.subscriptionId}`,
          reference: t('sales.types.subscriptionPayment')
        };
      case 'loan':
        return {
          label: t('sales.types.loan'),
          icon: '💰',
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          linkTo: `/loans/${sale.loanId}`,
          reference: t('sales.types.loanPayment')
        };
      default:
        return {
          label: t('sales.types.standalone'),
          icon: '💳',
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          linkTo: null,
          reference: t('sales.types.directSale')
        };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-xl p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-danger-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-danger-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-danger-800">Error loading sale</h3>
            <p className="text-sm text-danger-700 mt-1">{error || 'Not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/sales"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sale {sale.invoiceNumber}</h1>
              <p className="mt-1 text-sm text-gray-600">#{sale.id.slice(0, 8)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
              {getStatusIcon(sale.status)}
              <span className="ml-2">{sale.status}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          {/* Summary */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                  <CurrencyDollarIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Total</p>
                  <p className="text-sm text-gray-600">{formatCurrency(sale.total)}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <CreditCardIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Payment Method</p>
                  <p className="text-sm text-gray-600">{formatPaymentMethod(sale.paymentMethod)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sale Type & Associations */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Sale Type & Associations</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Sale Type</p>
                  {(() => {
                    const typeInfo = getSaleTypeDisplay(sale);
                    return (
                      <div className="flex items-center space-x-2">
                        <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeInfo.bgColor} ${typeInfo.color}`}>
                          <span className="mr-1">{typeInfo.icon}</span>
                          {typeInfo.label}
                        </div>
                      </div>
                    );
                  })()}
                  {(() => {
                    const typeInfo = getSaleTypeDisplay(sale);
                    return typeInfo.linkTo ? (
                      <Link to={typeInfo.linkTo} className="text-xs text-primary-600 hover:text-primary-900">
                        {typeInfo.reference}
                      </Link>
                    ) : (
                      <p className="text-xs text-gray-500">{typeInfo.reference}</p>
                    );
                  })()}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <UserIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Customer</p>
                  <p className="text-sm text-gray-600">{sale.customerName || '—'}</p>
                  {sale.customerEmail && <p className="text-xs text-gray-500">{sale.customerEmail}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Type-Specific Details */}
          {sale.orderId && sale.order && (
            <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ShoppingCartIcon className="h-5 w-5 mr-2" />
                Order Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-sm font-medium text-gray-700">Order Status:</span>
                  <div className="text-sm text-gray-900">{sale.order.status}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Order Type:</span>
                  <div className="text-sm text-gray-900">{sale.order.type}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Order Total:</span>
                  <div className="text-sm text-gray-900">{formatCurrency(sale.order.totalAmount)}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Assigned To:</span>
                  <div className="text-sm text-gray-900">
                    {sale.order.assignedToUser 
                      ? `${sale.order.assignedToUser.firstName} ${sale.order.assignedToUser.lastName}`.trim() || 'Unknown'
                      : 'Unknown'}
                  </div>
                </div>
              </div>
              
              {sale.order.items && sale.order.items.length > 0 && (
                <div>
                  <h3 className="text-md font-medium text-gray-900 mb-2">Order Items</h3>
                  <div className="space-y-2">
                    {sale.order.items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div>
                          <span className="text-sm font-medium">{item.product?.name || 'Unknown Product'}</span>
                          <div className="text-xs text-gray-500">
                            Qty: {item.quantity} × {formatCurrency(item.unitPrice)}
                            {item.discount > 0 && ` - ${formatCurrency(item.discount)} discount`}
                          </div>
                        </div>
                        <span className="text-sm font-medium">{formatCurrency(item.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {sale.subscriptionId && sale.subscription && (
            <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CalendarDaysIcon className="h-5 w-5 mr-2" />
                Subscription Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-700">Subscription ID:</span>
                  <div className="text-sm text-gray-900">#{sale.subscription.id.slice(0, 8)}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Status:</span>
                  <div className="text-sm text-gray-900">{sale.subscription.status}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Payment Status:</span>
                  <div className="text-sm text-gray-900">{sale.subscription.paymentStatus}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Total Amount:</span>
                  <div className="text-sm text-gray-900">{formatCurrency(sale.subscription.totalAmount)}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Start Date:</span>
                  <div className="text-sm text-gray-900">{new Date(sale.subscription.startDate).toLocaleDateString()}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">End Date:</span>
                  <div className="text-sm text-gray-900">{new Date(sale.subscription.endDate).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          )}

          {sale.loanId && sale.loan && (
            <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BanknotesIcon className="h-5 w-5 mr-2" />
                Loan Payment Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-700">Loan ID:</span>
                  <div className="text-sm text-gray-900">#{sale.loan.id.slice(0, 8)}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Borrower:</span>
                  <div className="text-sm text-gray-900">{sale.loan.borrower?.name || 'Unknown'}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Loan Status:</span>
                  <div className="text-sm text-gray-900">{sale.loan.status}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Principal Amount:</span>
                  <div className="text-sm text-gray-900">{formatCurrency(sale.loan.principalAmount)}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Current Balance:</span>
                  <div className="text-sm text-gray-900">{formatCurrency(sale.loan.currentBalance)}</div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Interest Rate:</span>
                  <div className="text-sm text-gray-900">{(sale.loan.monthlyInterestRate * 100).toFixed(2)}%/month</div>
                </div>
              </div>
              
              {/* Payment breakdown from notes if available */}
              {sale.notes && sale.notes.includes('Principal:') && (
                <div className="mt-4">
                  <h3 className="text-md font-medium text-gray-900 mb-2">Payment Breakdown</h3>
                  <div className="text-sm text-gray-700 whitespace-pre-line bg-gray-50 p-3 rounded">
                    {sale.notes}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {sale.notes && (
            <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{sale.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Sale Details</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Invoice</span><span className="font-medium">{sale.invoiceNumber}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Status</span><span className="font-medium">{sale.status}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Created</span><span className="font-medium">{formatDateTime(sale.createdAt)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Completed</span><span className="font-medium">{formatDateTime(sale.completedAt)}</span></div>
              <div className="flex justify-between">
                <span className="text-gray-600">Completed By</span>
                <span className="font-medium">
                  {sale.user 
                    ? `${sale.user.firstName} ${sale.user.lastName}`.trim() || 'Unknown'
                    : 'Unknown'}
                </span>
              </div>
              {sale.currencyCode && (
                <div className="flex justify-between"><span className="text-gray-600">Currency</span><span className="font-medium">{sale.currencyCode}</span></div>
              )}
              {sale.exchangeRate && sale.exchangeRate !== 1 && (
                <div className="flex justify-between"><span className="text-gray-600">Exchange Rate</span><span className="font-medium">{sale.exchangeRate}</span></div>
              )}
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Payment Amount</span><span className="font-medium text-lg">{formatCurrency(sale.total)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Payment Method</span><span className="font-medium">{formatPaymentMethod(sale.paymentMethod)}</span></div>
              {sale.order && (
                <>
                  <hr className="my-2" />
                  <div className="flex justify-between"><span className="text-gray-600">Order Subtotal</span><span className="font-medium">{formatCurrency(sale.order.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Order Tax</span><span className="font-medium">{formatCurrency(sale.order.tax)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Order Discount</span><span className="font-medium">-{formatCurrency(sale.order.discount)}</span></div>
                  <div className="flex justify-between border-t pt-2"><span className="text-gray-600 font-medium">Order Total</span><span className="font-semibold">{formatCurrency(sale.order.totalAmount)}</span></div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaleDetail;


